/**
 * BPEntryForm - Blood Pressure Entry Form Component
 *
 * Features:
 * - Morning/Night reading type toggle
 * - Automatic loading of existing readings for today
 * - Pre-population of form with existing data
 * - Systolic and diastolic input with validation
 * - Optional pulse rate and notes
 * - Real-time validation feedback
 * - Persistent form values after save (enables immediate editing)
 * - Success label notification after save
 * - Smart save: creates new reading or updates existing one
 */

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createBPReading, getTodayBPReadings, updateBPReading } from '../../services/api';
import type { BPReadingInput, ReadingType, BPGuidance } from '../../types/bloodPressure';
import BPGuidanceModal from './BPGuidanceModal';

interface BPEntryFormProps {
  onSuccess?: () => void;
}

const BPEntryForm: React.FC<BPEntryFormProps> = ({ onSuccess }) => {
  const [readingType, setReadingType] = useState<ReadingType>('morning');
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [pulse, setPulse] = useState('');
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [currentReadingId, setCurrentReadingId] = useState<string | null>(null);
  const [guidanceData, setGuidanceData] = useState<BPGuidance | null>(null);

  const queryClient = useQueryClient();

  // Fetch today's BP readings
  const { data: todayReadings } = useQuery({
    queryKey: ['bp-readings-today'],
    queryFn: getTodayBPReadings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Load existing reading when data arrives or reading type changes
  useEffect(() => {
    if (todayReadings) {
      const existingReading = todayReadings.find(
        (reading) => reading.reading_type === readingType
      );

      if (existingReading) {
        // Pre-populate form with existing data
        setSystolic(existingReading.systolic.toString());
        setDiastolic(existingReading.diastolic.toString());
        setPulse(existingReading.pulse ? existingReading.pulse.toString() : '');
        setNotes(existingReading.notes || '');
        setCurrentReadingId(existingReading._id || existingReading.id);
        setIsSaved(true);
      } else {
        // Clear form for new reading
        setSystolic('');
        setDiastolic('');
        setPulse('');
        setNotes('');
        setCurrentReadingId(null);
        setIsSaved(false);
      }
    }
  }, [todayReadings, readingType]);

  const saveMutation = useMutation({
    mutationFn: (data: BPReadingInput) => {
      if (currentReadingId) {
        // Update existing reading
        return updateBPReading(currentReadingId, data);
      } else {
        // Create new reading
        return createBPReading(data);
      }
    },
    onSuccess: (data) => {
      // Invalidate queries to refetch BP data
      queryClient.invalidateQueries({ queryKey: ['bp-readings'] });
      queryClient.invalidateQueries({ queryKey: ['bp-readings-today'] });
      queryClient.invalidateQueries({ queryKey: ['bp-trends'] });

      // Update reading ID and mark as saved
      setCurrentReadingId(data._id || data.id);
      setValidationError(null);
      setIsSaved(true);

      // Show guidance modal if present in response
      if (data.guidance) {
        setGuidanceData(data.guidance);
      }

      // Show success message (via parent component or toast)
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save BP reading';
      setValidationError(errorMsg);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    const systolicNum = parseInt(systolic);
    const diastolicNum = parseInt(diastolic);

    // Client-side validation
    if (!systolic || !diastolic) {
      setValidationError('Systolic and diastolic values are required');
      return;
    }

    if (isNaN(systolicNum) || isNaN(diastolicNum)) {
      setValidationError('Please enter valid numbers');
      return;
    }

    if (systolicNum < 60 || systolicNum > 250) {
      setValidationError('Systolic must be between 60-250 mmHg');
      return;
    }

    if (diastolicNum < 40 || diastolicNum > 150) {
      setValidationError('Diastolic must be between 40-150 mmHg');
      return;
    }

    if (systolicNum <= diastolicNum) {
      setValidationError('Systolic must be greater than diastolic');
      return;
    }

    if (pulse && (parseInt(pulse) < 40 || parseInt(pulse) > 200)) {
      setValidationError('Pulse must be between 40-200 bpm');
      return;
    }

    if (notes && notes.length > 500) {
      setValidationError('Notes must be 500 characters or less');
      return;
    }

    // Build data object - exclude reading_type for updates (backend doesn't allow changing it)
    const data: any = {
      systolic: systolicNum,
      diastolic: diastolicNum,
      ...(pulse && { pulse: parseInt(pulse) }),
      ...(notes && { notes })
    };

    // Include reading_type only for new readings (not updates)
    if (!currentReadingId) {
      data.reading_type = readingType;
    }

    saveMutation.mutate(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="card shadow-elegant">
        <h2 className="text-2xl font-serif font-semibold text-neutral-800 mb-6">
          Record Blood Pressure
        </h2>

      {/* Reading Type Toggle */}
      <div className="flex mb-6 bg-primary-50 rounded-full p-1.5">
        <button
          type="button"
          onClick={() => setReadingType('morning')}
          className={`flex-1 py-3 px-6 rounded-full font-medium transition-all duration-300 ${
            readingType === 'morning'
              ? 'bg-white text-primary-700 shadow-md'
              : 'text-primary-600 hover:text-primary-800'
          }`}
        >
          ☀️ Morning
        </button>
        <button
          type="button"
          onClick={() => setReadingType('night')}
          className={`flex-1 py-3 px-6 rounded-full font-medium transition-all duration-300 ${
            readingType === 'night'
              ? 'bg-white text-primary-700 shadow-md'
              : 'text-primary-600 hover:text-primary-800'
          }`}
        >
          🌙 Night
        </button>
      </div>

      {/* Error Message */}
      {validationError && (
        <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
          <p className="text-sm text-red-800 font-medium">{validationError}</p>
        </div>
      )}

      {/* Systolic and Diastolic Inputs */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="systolic" className="block text-sm font-medium text-neutral-700 mb-2">
            Systolic (mmHg) <span className="text-red-500">*</span>
          </label>
          <input
            id="systolic"
            type="number"
            value={systolic}
            onChange={(e) => {
              setSystolic(e.target.value);
              setIsSaved(false);
            }}
            className="input input-large"
            placeholder="120"
            min="60"
            max="250"
            required
          />
          <p className="text-xs text-neutral-500 mt-1">Range: 60-250</p>
        </div>
        <div>
          <label htmlFor="diastolic" className="block text-sm font-medium text-neutral-700 mb-2">
            Diastolic (mmHg) <span className="text-red-500">*</span>
          </label>
          <input
            id="diastolic"
            type="number"
            value={diastolic}
            onChange={(e) => {
              setDiastolic(e.target.value);
              setIsSaved(false);
            }}
            className="input input-large"
            placeholder="80"
            min="40"
            max="150"
            required
          />
          <p className="text-xs text-neutral-500 mt-1">Range: 40-150</p>
        </div>
      </div>

      {/* Optional Pulse */}
      <div className="mb-4">
        <label htmlFor="pulse" className="block text-sm font-medium text-neutral-700 mb-2">
          Pulse (bpm) - Optional
        </label>
        <input
          id="pulse"
          type="number"
          value={pulse}
          onChange={(e) => {
            setPulse(e.target.value);
            setIsSaved(false);
          }}
          className="input"
          placeholder="72"
          min="40"
          max="200"
        />
        <p className="text-xs text-neutral-500 mt-1">Range: 40-200 (if provided)</p>
      </div>

      {/* Optional Notes */}
      <div className="mb-6">
        <label htmlFor="notes" className="block text-sm font-medium text-neutral-700 mb-2">
          Notes - Optional
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setIsSaved(false);
          }}
          className="input"
          placeholder="e.g., after exercise, felt dizzy"
          maxLength={500}
          rows={3}
        />
        <p className="text-xs text-neutral-500 mt-1">
          {notes.length}/500 characters
        </p>
      </div>

      {/* Success Message */}
      {isSaved && (
        <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-400 rounded-r-lg">
          <p className="text-sm text-green-800 font-medium">✓ Reading Saved</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={saveMutation.isPending}
        className="btn btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saveMutation.isPending ? 'Saving...' : isSaved ? 'Edit Reading' : 'Save Reading'}
      </button>
      </form>

      {/* Guidance Modal */}
      <BPGuidanceModal
        guidance={guidanceData}
        onClose={() => setGuidanceData(null)}
      />
    </>
  );
};

export default BPEntryForm;
